/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useState } from "react";
import { Box, Button, Grid, TextField, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import { useFormik } from "formik";
import { RootState, useAppDispatch } from "@store/store";
import CustomUploadFileV2 from "@components/CustomUploadFileV2";
import { useLazyGetBanksByCodeQuery, useLazyGetTaxSystemsQuery } from "@api/catalogs";
import {
  useLazyGetBankAccountsQuery,
  usePostBankAccountsMutation,
  usePostBankAccountStatementMutation,
  usePutBankAccountMutation,
  useDeleteBankAccountStatementMutation,
} from "@api/applications";
import { useLazyGetDocumentAppIdQuery } from "@api/documents";
import { InactivityHandler } from "@components/InactivityHandler";
import { UUID } from "crypto";
import { DocumentResponse, PersonaStructure } from "@interfaces/response";
import { PayloadBankAccount } from "@interfaces/request";
import { ApiError } from "@interfaces/response";
import { usePatchPersonMutation } from "@api/personasApi";
import { DOCUMENTS_CATALOG, DEFAULT_NUMBER_BANK_STATEMENTS } from "@helpers/constantes";
import CustomRightImage from "@components/CustomRightImagev2";
import CenteredLoader from "@components/CenteredLoader";
import { setModalErrorVisible } from "@store/slices/appSlice";
import * as Sentry from "@sentry/react";
import {
  createEmptyBankAccount,
  getFormikArrayError,
  identifyBankAccounts,
  getBankInformationConfig,
  getBankStatementIndex,
} from "@helpers/funciones";
import useGenerateAnexo from "@hooks/useGenerateAnexo";
import { BankAccountFormItem, BankInformationConfig } from "@interfaces/catalogs";

const PERSON_BANK_ROUTE_CONFIG: Record<
  string,
  { configKey: keyof BankInformationConfig; generateAnexo: boolean; backPath: string }
> = {
  info_bancaria_aval: {
    configKey: "aval",
    generateAnexo: true,
    backPath: "/producto/creditosimple/docs_aval",
  },
  info_bancaria_aval_adicional: {
    configKey: "aval",
    generateAnexo: false,
    backPath: "/producto/creditosimple/docs_aval",
  },
  info_bancaria_representante: {
    configKey: "legal_representative",
    generateAnexo: false,
    backPath: "/producto/creditosimple/docs_representante",
  },
};

const getPersonBankRouteConfig = (pathname: string) => {
  if (pathname.includes("info_bancaria_aval_adicional")) {
    return PERSON_BANK_ROUTE_CONFIG.info_bancaria_aval_adicional;
  }
  if (pathname.includes("info_bancaria_representante")) {
    return PERSON_BANK_ROUTE_CONFIG.info_bancaria_representante;
  }
  return PERSON_BANK_ROUTE_CONFIG.info_bancaria_aval;
};

export const InformacionBancariaPersona = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { personId } = useParams<{ personId: string }>();
  const { configKey, generateAnexo: shouldGenerateAnexo, backPath } = getPersonBankRouteConfig(pathname);
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useAppDispatch();

  const application = useSelector((state: RootState) => state.register.application);
  const personalData = useSelector((state: RootState) => state.register.personal_data);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [loadingBankName, setLoadingBankName] = useState<boolean>(false);
  const [loadingBankStatement, setLoadingBankStatement] = useState<boolean>(false);
  const [totalStatements, setTotalStatements] = useState(DEFAULT_NUMBER_BANK_STATEMENTS);
  const [requiredStatements, setRequiredStatements] = useState(DEFAULT_NUMBER_BANK_STATEMENTS);

  const [triggerGetBanks] = useLazyGetBanksByCodeQuery();
  const [getTaxSystems] = useLazyGetTaxSystemsQuery();
  const [getBankAccounts] = useLazyGetBankAccountsQuery();
  const [getDocumentsByAppId] = useLazyGetDocumentAppIdQuery();
  const [triggerPutBankAccount] = usePutBankAccountMutation();
  const [triggerPostBankAccount] = usePostBankAccountsMutation();
  const [patchPersonData] = usePatchPersonMutation();
  const [triggerPostBankAccountStatement] = usePostBankAccountStatementMutation();
  const [triggerDeleteBankAccountStatement] = useDeleteBankAccountStatementMutation();
  const generateAnexo = useGenerateAnexo();

  const formik = useFormik({
    initialValues: {
      accounts: [createEmptyBankAccount({ number_statements: DEFAULT_NUMBER_BANK_STATEMENTS })],
    },
    validationSchema: yup.object().shape({
      accounts: yup.array().of(
        yup.object().shape({
          clabe: yup
            .string()
            .min(18, "Formato incorrecto, debe tener 18 dígitos")
            .required("Campo requerido.")
            .test("unique", "La cuenta bancaria ya se encuentra registrada.", function (value) {
              const num_results: number = formik.values.accounts.filter(
                (item: BankAccountFormItem) => item.clabe === value,
              ).length;
              return num_results > 1 ? false : true;
            }),
          bank_id: yup.string().required("Clabe bancaria incorrecta, validar nuevamente."),
          statements: yup.array().of(
            yup.object().shape({
              bank_statement_document_id: yup
                .string()
                .nullable()
                .test("required-if-min", "Campo requerido.", function (value, params) {
                  return getBankStatementIndex(params.path) < requiredStatements ? Boolean(value) : true;
                }),
              document_id: yup
                .string()
                .nullable()
                .test("required-if-min", "Campo requerido.", function (value, params) {
                  return getBankStatementIndex(params.path) < requiredStatements ? Boolean(value) : true;
                }),
              document_order: yup
                .number()
                .nullable()
                .test("required-if-min", "Campo requerido.", function (value, params) {
                  return getBankStatementIndex(params.path) < requiredStatements ? value != null : true;
                }),
              document_value: yup.object().nullable(),
            }),
          ),
        }),
      ),
    }),
    onSubmit: async (_values, { setSubmitting }) => {
      try {
        setSubmitting(true);
        if (!personId) {
          navigate(backPath);
          return;
        }

        const response_patch = await patchPersonData({
          personId,
          body: {
            bank_references_completed: true,
            bank_references_status: "COMPLETO",
          },
        }).unwrap();

        if (shouldGenerateAnexo) {
          await generateAnexo(application.id, response_patch.data, "anexo_1");
        }

        navigate(backPath);
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            component: "InformacionBancariaPersona-submit",
          },
        });
        const api_error = error as ApiError;
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setSubmitting(false);
      }
    },
    enableReinitialize: true,
    validateOnMount: true,
  });

  const getBankNameByCode = useCallback(async (bank_code: string) => {
    try {
      const bank_info = await triggerGetBanks(bank_code).unwrap();
      if (bank_info) {
        return {
          id: bank_info.id,
          name: bank_info.name,
        };
      } else {
        return null;
      }
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          component: "InformacionBancariaPersona-getBankNameByCode",
        },
      });
      return null;
    }
  }, []);

  const handleChangeCLABE = async (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const { name, value } = event.target;
    const account = formik.values.accounts[index];
    const only_numbers = value.replace(/[^0-9]/g, "");
    formik.setFieldValue(name, only_numbers);

    if (only_numbers.length === 18) {
      setLoadingBankName(true);
      try {
        const bank_code = only_numbers.substring(0, 3);
        const bank_info = await getBankNameByCode(bank_code);

        if (bank_info) {
          const payload: PayloadBankAccount = {
            loan_application_id: application.id,
            client_details_id: null,
            person_id: personId,
            account: 0,
            account_holder: "",
            clabe: only_numbers,
            bank_id: bank_info.id,
            documents: [],
          };

          if (account.id) {
            await triggerPutBankAccount({
              solicitudId: application.id,
              bankAccountId: account.id,
              body: payload,
            }).unwrap();
          } else {
            const response = await triggerPostBankAccount({
              solicitudId: application.id,
              body: payload,
            }).unwrap();

            formik.setFieldValue(`accounts[${index}].id`, response.id);
          }

          formik.setFieldValue(`accounts[${index}].bank_id`, bank_info.id);
          formik.setFieldValue(`accounts[${index}].bank_name`, bank_info.name || "");
        } else {
          formik.setFieldValue(`accounts[${index}].bank_id`, "");
          formik.setFieldValue(`accounts[${index}].bank_name`, "");
        }

        setTimeout(() => {
          formik.validateForm();
          formik.setFieldTouched(`accounts[${index}].bank_id`, true);
          formik.setFieldTouched(`accounts[${index}].bank_name`, true);
        }, 500);
      } finally {
        setLoadingBankName(false);
      }
    }
  };

  const handleUploadBankStatement = async (
    newUpload: DocumentResponse | undefined,
    accountIndex: number,
    statementIndex: number,
  ) => {
    try {
      setLoadingBankStatement(true);

      const copy_accounts: BankAccountFormItem[] = [...formik.values.accounts];
      const account: BankAccountFormItem = copy_accounts[accountIndex];
      const statement = account.statements[statementIndex];

      if (newUpload) {
        if (account.id) {
          const response = await triggerPostBankAccountStatement({
            solicitudId: application.id,
            accountId: account.id,
            body: {
              loan_application_bank_account_id: account.id,
              document_id: newUpload.id,
              document_order: statementIndex + 1,
            },
          }).unwrap();

          copy_accounts[accountIndex].statements[statementIndex] = {
            bank_statement_document_id: response.id,
            document_id: newUpload.id,
            document_order: statementIndex + 1,
            document_value: newUpload,
          };

          formik.setFieldValue("accounts", copy_accounts);
        }
      } else {
        if (account.id && statement.bank_statement_document_id) {
          await triggerDeleteBankAccountStatement({
            solicitudId: application.id,
            accountId: account.id,
            documentId: statement.bank_statement_document_id,
          }).unwrap();

          copy_accounts[accountIndex].statements[statementIndex] = {
            bank_statement_document_id: undefined,
            document_id: undefined,
            document_order: statementIndex + 1,
            document_value: undefined,
          };

          formik.setFieldValue("accounts", copy_accounts);
        }
      }
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          component: "InformacionBancariaPersona-handleUploadBankStatement",
        },
      });
    } finally {
      setLoadingBankStatement(false);
    }
  };

  const getInitialData = async () => {
    try {
      setIsLoading(true);
      if (!personId) {
        navigate(backPath);
        return;
      }

      const [tax_systems, list_bank_accounts, documents] = await Promise.all([
        getTaxSystems(application.type).unwrap(),
        getBankAccounts(application.id).unwrap(),
        getDocumentsByAppId({
          loanApplicationId: application.id,
          personId,
          status: true,
        }).unwrap(),
      ]);

      const { total_items, required_items } = getBankInformationConfig(
        tax_systems?.config_extra?.bank_information_config?.[configKey],
      );

      setTotalStatements(total_items);
      setRequiredStatements(required_items);

      const { aval_accounts } = identifyBankAccounts({
        bank_accounts: list_bank_accounts,
        aval: { id: personId } as PersonaStructure,
      });

      const accounts: BankAccountFormItem[] = [];

      for (let index = 0; index < aval_accounts.length; index++) {
        const account = aval_accounts[index];

        const bank_code = (account.clabe || "").toString().substring(0, 3);
        const bank_info = await getBankNameByCode(bank_code);

        const new_account: BankAccountFormItem = {
          ...createEmptyBankAccount({ number_statements: total_items }),
          id: account.id,
          clabe: account.clabe || "",
          bank_id: bank_info?.id || "",
          bank_name: bank_info?.name || "",
        };

        for (let statementIndex = 0; statementIndex < total_items; statementIndex++) {
          const account_document = account.documents[statementIndex];
          if (account_document) {
            new_account.statements[statementIndex] = {
              bank_statement_document_id: account_document.bank_statement_document_id,
              document_id: account_document.document_id,
              document_order: account_document.document_order || 0,
              document_value: documents.find((item) => item.id === account_document.document_id),
            };
          }
        }

        accounts.push(new_account);
      }

      formik.setFieldValue(
        "accounts",
        accounts.length ? accounts : [createEmptyBankAccount({ number_statements: total_items })],
      );
    } catch (error) {
      const api_error = error as ApiError;
      Sentry.captureException(error, {
        extra: {
          component: "InformacionBancariaPersona-initial_fetch",
        },
      });
      dispatch(setModalErrorVisible({ open: true, error: api_error }));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getInitialData();
  }, [personId]);

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8}>
        <InactivityHandler />
        {isLoading ? (
          <CenteredLoader />
        ) : (
          <Box maxWidth="sm" mx="auto" mt={4} px={2}>
            <Typography
              textAlign={"center"}
              variant={isMobileOrTablet ? "body2" : "body1"}
              fontWeight={700}
              color={theme?.palette?.primary?.dark}
            >
              Información bancaria
            </Typography>

            <Typography
              textAlign={"center"}
              variant={isMobileOrTablet ? "body2" : "body1"}
              color={theme?.palette?.primary?.dark}
              mt={2}
            >
              <strong>1</strong> de 1
            </Typography>

            <Typography
              textAlign={"center"}
              color={theme?.palette?.primary?.main}
              variant={isMobileOrTablet ? "body2" : "body1"}
              fontWeight={600}
              mt={2}
            >
              Ingresa los datos de tu cuenta principal, dónde concentras la mayoría de tu actividad bancaría.
            </Typography>

            <form onSubmit={formik.handleSubmit}>
              {formik.values.accounts.map((account: BankAccountFormItem, index: number) => (
                <Box key={index} mt={index > 0 ? 4 : 0}>
                  <TextField
                    name={`accounts[${index}].clabe`}
                    label="Cuenta CLABE"
                    margin="normal"
                    type="text"
                    fullWidth
                    autoComplete="off"
                    onBlur={formik.handleBlur}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleChangeCLABE(event, index)}
                    value={formik.values.accounts[index].clabe}
                    error={
                      Boolean(getFormikArrayError(formik, "accounts", index, "clabe")) ||
                      Boolean(getFormikArrayError(formik, "accounts", index, "bank_id"))
                    }
                    helperText={
                      getFormikArrayError(formik, "accounts", index, "clabe") ||
                      getFormikArrayError(formik, "accounts", index, "bank_id")
                    }
                    inputProps={{ maxLength: 18 }}
                    disabled={isLoading || formik.isSubmitting || loadingBankName}
                  />

                  <TextField
                    name={`accounts[${index}].bank_name`}
                    label="Banco"
                    margin="normal"
                    fullWidth
                    autoComplete="off"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.accounts[index].bank_name}
                    disabled={true}
                  />

                  <Box my={2}>
                    <Typography
                      variant={isMobileOrTablet ? "body2" : "body1"}
                      fontWeight={theme?.palette?.fontWeights?.regular}
                      color={theme?.palette?.primary?.dark}
                    >
                      Debes cargar los estado de cuenta bancario de los últimos {totalStatements} meses de una o mas
                      cuentas para cubrir la capacidad de pago necesaria para el crédito solicitado. Completa al menos{" "}
                      {requiredStatements}.
                    </Typography>
                  </Box>

                  {account.statements.map((statement, statementIndex) => (
                    <Box key={`bank_statement_${index}_${statementIndex}`}>
                      <CustomUploadFileV2
                        mt={2}
                        inputName={`bank_statement_${index}_${statementIndex}`}
                        uploadedFile={statement?.document_value}
                        setUploadedFile={(newUpload) => {
                          handleUploadBankStatement(newUpload, index, statementIndex);
                        }}
                        optionalName={`Estado de cuenta ${statementIndex + 1}`}
                        optionalDescription=""
                        metadata={{
                          fileCode: DOCUMENTS_CATALOG.estado_cuenta_bancario.code,
                          uploadType: "person",
                          personId: personId as UUID,
                          clientDetailsId: personalData.id,
                        }}
                        disabled={isLoading || formik.isSubmitting || loadingBankStatement || !account.id}
                        required={statementIndex < requiredStatements}
                      />
                    </Box>
                  ))}
                </Box>
              ))}

              <Grid container columnSpacing={1} my={4}>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="blue_outlined"
                    sx={{ padding: "0px", height: "100%" }}
                    onClick={() => navigate(backPath)}
                    disabled={isLoading || formik.isSubmitting || loadingBankStatement}
                  >
                    Regresar al inicio
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    type="submit"
                    variant="default"
                    disabled={isLoading || !formik.isValid || formik.isSubmitting}
                  >
                    Continuar
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Box>
        )}
      </Grid>
      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default InformacionBancariaPersona;
